/**
 * VE-30856 — rendering the "somebody else has this open" refusal.
 *
 * The guards in ./services decide WHAT to say and dispatch it as ids plus
 * values; this turns that into the node the snackbar shows. It lives apart
 * from the sagas because it is the only part that needs JSX, and in this
 * module rather than with the snackbar component because the message family is
 * the guards' own.
 *
 * TWO CLAUSES, NOT ONE SENTENCE. The lead says what was refused and therefore
 * has to track the VERB — "Cannot delete" is wrong when the reviewer pressed
 * Move to trash, which is how this started. The tail says which file and whom
 * to ask. Splitting them keeps three leads × a handful of tails instead of a
 * sentence per combination, and gives the snackbar two readable lines rather
 * than one long one.
 *
 * THE NAMES ARE EMPHASISED, not marked up in the message text: they are passed
 * in as elements, so the catalogues keep plain `{fileName}` placeholders and a
 * translator cannot break a tag. `anotherUser` is substituted here, the one
 * place every holder-naming message passes through.
 */
import { Children, type ReactNode } from 'react';
import type { IntlShape } from 'react-intl';

/** The values worth setting apart from the surrounding sentence. */
const EMPHASISED = ['requestName', 'fileName', 'name'];

/**
 * Fallbacks for ids the guards choose at runtime, which is why they cannot be
 * written inline the way every other `formatMessage` call here writes them.
 * The live text is the catalogue's (`i18n/messages/en-US`); these only stand
 * in if a key goes missing, instead of rendering the id.
 *
 * Kept honest by lockRefusalMessage.test.tsx, which formats through an `intl`
 * that THROWS on a missing translation — so a key that exists only here, or
 * only in one locale, fails the suite rather than silently showing a French
 * reviewer English.
 */
const DEFAULTS: Record<string, string> = {
  lockLeadDelete: 'Cannot delete',
  lockLeadTrash: 'Cannot move to trash',
  lockLeadUpdate: 'Cannot update',
  lockTailRequestFile:
    '{requestName} contains {fileName} (ID {tdoId}), which {name} is currently editing.',
  lockTailRequestFileById:
    '{requestName} contains a file (ID {tdoId}) that {name} is currently editing.',
  lockTailFile: '{fileName} (ID {tdoId}) is currently being edited by {name}.',
  lockTailFileById:
    'the file with ID {tdoId} is currently being edited by {name}.',
  lockTailInUse: 'a file is currently in use.',
  lockTailJobsProcessing:
    'some files are still processing. Please try again later.',
  lockTailUnverifiable:
    'we could not check whether the files are in use. Please try again.',
};

export interface LockRefusal {
  /** What was refused, by intent: delete, move to trash, update. */
  readonly leadId: string;
  /** Which file is open, and who has it. */
  readonly tailId: string;
  readonly values?: Record<string, string>;
}

/**
 * `formatMessage` returns an array once any value is an element. Children
 * .toArray keys it, which a bare array render would warn about.
 */
const clause = (
  intl: IntlShape,
  id: string,
  values: Record<string, ReactNode>
): ReactNode => {
  const formatted = intl.formatMessage(
    // The id is the guard's verdict, picked at runtime, so there is no literal
    // to write here; DEFAULTS above is this call's table of them.
    // eslint-disable-next-line formatjs/enforce-default-message
    { id, defaultMessage: DEFAULTS[id] },
    values
  );
  return (
    <>{Children.toArray(Array.isArray(formatted) ? formatted : [formatted])}</>
  );
};

export const renderLockRefusal = (
  intl: IntlShape,
  { leadId, tailId, values }: LockRefusal
): ReactNode => {
  const resolved: Record<string, ReactNode> = {};
  for (const [key, value] of Object.entries(values ?? {})) {
    // A lock record whose name never got written would otherwise read as
    // "edited by " — see lockHolderName, which blanks a half-written one.
    const text =
      key === 'name' && !value
        ? intl.formatMessage({
            id: 'anotherUser',
            defaultMessage: 'another user',
          })
        : value;
    resolved[key] = EMPHASISED.includes(key) ? <strong>{text}</strong> : text;
  }

  return (
    <span>
      <span style={{ display: 'block' }}>{clause(intl, leadId, resolved)}</span>
      {clause(intl, tailId, resolved)}
    </span>
  );
};
